import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { useStore } from "../store/globalState";
import { TITLE_COLOR } from "../constants";
import { LEGAL_DOC_KEYS, type LegalDocKey } from "../constants/legal";
import { LegalAccordion } from "../components/legal/LegalAccordion";
import { LegalContent } from "../components/legal/LegalContent";
import "../styles/legal.style.css";
import { getEvents, safeCapture } from "../analytics";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";


/**
 * Legal document viewer. Renders a single document when `?doc=` is present, or
 * an accordion of all documents otherwise (optionally excluding one via
 * `?exclude=`). Backed by `GET /widget/legal/:doc/:lang`.
 */
export const LegalPage = () => {
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy } = colorConfig;
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    safeCapture(getEvents().LEGAL_VIEW);
  }, []);
  const [searchParams] = useSearchParams();

  const docParam = searchParams.get("doc") as LegalDocKey | null;
  const excludeParam = searchParams.get("exclude") as LegalDocKey | null;

  const filteredDocs = excludeParam
    ? LEGAL_DOC_KEYS.filter((k) => k !== excludeParam)
    : LEGAL_DOC_KEYS;

  const title = docParam ? t(`legal.docs.${docParam}`) : t("legal.title");

  return (
    <AuraLayout
      colorConfig={colorConfig}
      className="legal-page"
    >
      <Layout.Header>
        <Navbar
          onBackClick={() => navigate(-1)}
          title={title}
          align="center"
          color={TITLE_COLOR}
        />
      </Layout.Header>
      <Layout.Content isOverflowauto>
        <ScrollFadeIndicator color={bodyCopy} />
        <section className="legal-wrapper" style={{ color: bodyCopy }}>
          {docParam ? (
            <LegalContent doc={docParam} />
          ) : (
            <LegalAccordion docs={filteredDocs} />
          )}
        </section>
      </Layout.Content>
    </AuraLayout>
  );
};
